import * as React from 'react';
import PropTypes from 'prop-types';
import { EmojiEvents } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import { getTranslate } from 'react-localize-redux';

import { Button, ButtonBase, makeStyles, Typography } from '@material-ui/core';
import { contentElemenIdPrefix } from '../../../../constants';
import FormQuestion from '../../FormQuestion';

const useStyles = makeStyles(theme => ({
    root: {

    },
    results: {
        margin: theme.spacing(4, 0),
        boxShadow: theme.shadows[4],
        padding: theme.spacing(4, 2),
        background: theme.palette.common.white,
        display: 'flex',
        alignItems: 'center',

        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
        },
    },
    scoreHeading: {
        flex: '1 1 auto',
        color: theme.youwell.colors.green,
    },
    resetButton: {
        marginLeft: theme.spacing(1),

        [theme.breakpoints.down('xs')]: {
            marginTop: theme.spacing(2),
        },
    },
    actions: {
        margin: theme.spacing(4, 0),
        display: 'flex',
        justifyContent: 'center',
    },
    showScoreButton: {
        width: 400,
        padding: theme.spacing(2),
    },
    success: {
        marginRight: theme.spacing(2),
        fontSize: theme.typography.h6.fontSize,
        color: theme.youwell.colors.green,
        display: 'flex',
        alignItems: 'center',
    },
    successIcon: {
        marginLeft: theme.spacing(1),
    },
    resetButtonSmall: {
        position: 'absolute',
        padding: theme.spacing(1),
        right: 0,
        bottom: 32,
        '&:hover': {
            background: theme.palette.grey[50],
        },
    },
}), { name: 'Quiz' });

const Quiz = ({
    element, contentState, responses, handleResponseUpdate, disabled, readOnly, FormQuestionProps,
}) => {
    const classes = useStyles();
    const translate = useSelector(state => getTranslate(state.localize));

    if (!element) {
        return null;
    }

    const questions = element.questions || [];
    const elementSettings = element.settings || {};
    const resultQuestionId = Number(elementSettings.resultQuestionId) || 1000;

    const questionsWithAnswers = questions?.filter(d => d.answers?.length);
    const showScores = responses[resultQuestionId]?.response.scaleResponse === 1;

    const resetQuiz = () => {
        element?.questions?.forEach((q) => {
            handleResponseUpdate(q)('choiceResponse', null);
        });
        handleResponseUpdate({ id: resultQuestionId })('scaleResponse', 0);

        if (window.scrollTo) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            document.documentElement.scrollTop = 0;
        }
    };

    const showResults = () => {
        handleResponseUpdate({ id: resultQuestionId })('scaleResponse', 1);
    };

    const scoring = questionsWithAnswers.reduce((prev, curr) => (
        {
            score: prev.score + Number(responses[curr.id]?.response?.choiceResponse?.reduce((pResp, cResp) => pResp + cResp.score || 0, 0) || 0),
            max: prev.max + Number(curr.answers.reduce((pAnswer, cAnswer) => pAnswer + cAnswer.score || 0, 0)),
        }
    ), { score: 0, max: 0 });

    return (
        <div className={classes.root}>
            {questions.map(q => (
                <FormQuestion
                    key={q.id}
                    question={q}
                    response={responses[q.id]?.response}
                    handleResponseUpdate={handleResponseUpdate(q)}
                    // showRequired={showValidationErrors}
                    disabled={disabled}
                    contentState={contentState}
                    readOnly={readOnly}
                    elementSettings={elementSettings}
                    showScores={showScores}
                    {...FormQuestionProps}
                />
            ))}

            {showScores ? (
                <div className={classes.results}>
                    {scoring.score === scoring.max ? (
                        <>
                            <Typography variant="subtitle1" className={classes.success}>
                                {translate('taskContent.fullScore', scoring)}
                                <EmojiEvents className={classes.successIcon} />
                            </Typography>
                            <ButtonBase onClick={resetQuiz} className={classes.resetButtonSmall}>
                                <Typography variant="caption" color="textSecondary">
                                    {translate('taskContent.retakeQuiz')}
                                </Typography>
                            </ButtonBase>
                        </>
                    ) : (
                        <>
                            <Typography variant="subtitle1" className={classes.scoreHeading} id={`${contentElemenIdPrefix}${element.id}_quizScore`}>
                                {translate('taskContent.youGotxOfXScore', scoring)}
                            </Typography>
                            <Button onClick={resetQuiz} className={classes.resetButton} variant="contained" color="primary">
                                {translate('taskContent.retakeQuiz')}
                            </Button>
                        </>
                    )}
                </div>
            ) : (
                <div className={classes.actions}>
                    <Button onClick={showResults} className={classes.showScoreButton} variant="contained" color="secondary">
                        {translate('taskContent.showResults')}
                    </Button>
                </div>
            )}

        </div>
    );
};


Quiz.propTypes = {
    element: PropTypes.object.isRequired,
    taskId: PropTypes.string.isRequired,
    getProgramItem: PropTypes.func.isRequired,
    getSpecificTaskResponses: PropTypes.func.isRequired,
    handleResponseUpdate: PropTypes.func.isRequired,
    contentState: PropTypes.string,
    responses: PropTypes.object.isRequired,
    disabled: PropTypes.bool,
    FormQuestionProps: PropTypes.object,
    readOnly: PropTypes.bool,
};

Quiz.defaultProps = {
    contentState: null,
    FormQuestionProps: null,
    readOnly: false,
    disabled: false,
};

export default Quiz;
